import { useMutation, useQuery, type QueryClient } from "@tanstack/react-query";
import { queryClient } from "@/shared/services/queryClient";
import { toast } from "sonner";
import { normalizeError } from "@/shared/errors/normalizeError";
import {
  deliveryPlansApi,
  type CreateDeliveryPlanRequest,
  type UpdateDeliveryPlanRequest,
  type PublishDeliveryPlanRequest,
  type CancelDeliveryPlanRequest,
  type ReassignDeliveryPlanRequest,
  type CheckDeliveryPlanConflictsRequest,
  type DeliveryPlanListQuery,
  type PlannerEligibleOrderListQuery,
} from "../services/deliveryPlansApi";

const QK = {
  list: (query: DeliveryPlanListQuery) => ["delivery-plans", query],
  detail: (id: string) => ["delivery-plans", id],
  eligibleOrders: (query: PlannerEligibleOrderListQuery) => ["delivery-plans-eligible-orders", query],
} as const;

const getErrorMessage = (err: unknown, fallback: string) => {
  const envelope = normalizeError(err);
  return envelope.userMessage || fallback;
};

export const refreshDeliveryPlanQueries = (client: QueryClient) =>
  Promise.all([
    client.invalidateQueries({ queryKey: ["delivery-plans"], refetchType: "active" }),
    client.invalidateQueries({ queryKey: ["delivery-plans-eligible-orders"], refetchType: "active" }),
  ]);

export const useDeliveryPlansPaged = (query: DeliveryPlanListQuery, enabled = true) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => deliveryPlansApi.getPaged(query),
    enabled,
  });

export const useDeliveryPlanDetail = (id: string) =>
  useQuery({
    queryKey: QK.detail(id),
    queryFn: () => deliveryPlansApi.getById(id),
    enabled: !!id,
  });

export const useEligibleOrders = (query: PlannerEligibleOrderListQuery) =>
  useQuery({
    queryKey: QK.eligibleOrders(query),
    queryFn: () => deliveryPlansApi.getEligibleOrders(query),
  });

export const useCreateDeliveryPlan = () =>
  useMutation({
    mutationFn: (data: CreateDeliveryPlanRequest) => deliveryPlansApi.create(data),
    onSuccess: async () => {
      await refreshDeliveryPlanQueries(queryClient);
      toast.success("Delivery plan created.");
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, "Failed to create delivery plan."));
    },
  });

export const useUpdateDeliveryPlan = () =>
  useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateDeliveryPlanRequest }) =>
      deliveryPlansApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["delivery-plans"] });
      queryClient.invalidateQueries({ queryKey: ["delivery-plans-eligible-orders"] });
      toast.success("Delivery plan updated.");
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, "Failed to update delivery plan."));
    },
  });

export const usePublishDeliveryPlan = () =>
  useMutation({
    mutationFn: ({ id, data }: { id: string; data: PublishDeliveryPlanRequest }) =>
      deliveryPlansApi.publish(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["delivery-plans"] });
      toast.success("Delivery plan published.");
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, "Failed to publish delivery plan."));
    },
  });

export const useCancelDeliveryPlan = () =>
  useMutation({
    mutationFn: ({ id, data }: { id: string; data: CancelDeliveryPlanRequest }) =>
      deliveryPlansApi.cancel(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["delivery-plans"] });
      queryClient.invalidateQueries({ queryKey: ["delivery-plans-eligible-orders"] });
      toast.success("Delivery plan cancelled; committed quantities are available to plan again.");
    },
    onError: (err) => toast.error(getErrorMessage(err, "Failed to cancel delivery plan.")),
  });

export const useReassignDeliveryPlan = () =>
  useMutation({
    mutationFn: ({ id, data }: { id: string; data: ReassignDeliveryPlanRequest }) =>
      deliveryPlansApi.reassign(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["delivery-plans"] });
      toast.success("Delivery plan reassigned.");
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, "Failed to reassign delivery plan."));
    },
  });

export const useCheckDeliveryPlanConflicts = () =>
  useMutation({
    mutationFn: (data: CheckDeliveryPlanConflictsRequest) => deliveryPlansApi.checkConflicts(data),
    onError: (err) => {
      toast.error(getErrorMessage(err, "Failed to check conflicts."));
    },
  });
